import dayjs from 'dayjs';
import { useListStore } from '@/stores/list';
import { useUsersStore } from '@/stores/users';
import { TDateFormat } from '@/types/date';
import {
  getDateFormat,
  getDayFormat,
  getTimeFormat,
  getTimeZoneOffsetInMinutes,
} from '@/utils/convertDate';

/**
 * Custom hook to process and format a given date with timezone adjustments and optional duration.
 * @param myDate - The input date, which can be a Date object, string, or dayjs object.
 * @param duration - Optional duration in minutes to add to the date.
 * @returns An object containing formatted date and time information:
 *  - displayDate: The adjusted dayjs object with timezone offset.
 *  - displayTime: Formatted time as "hh:mm A".
 *  - displayDay: Formatted day of the week.
 *  - displayDateFormat: Formatted full date.
 *  - displayDateWithDuration: dayjs object adjusted by the given duration.
 *  - displayTimeWithDuration: Formatted time after adding duration.
 *  - displayDayWithDuration: Formatted day after adding duration.
 *  - displayDateWithDurationFormat: Formatted full date after adding duration.
 *  - timeAgo: Relative time string (e.g., "2 hours ago").
 */
export const useDate = (myDate: TDateFormat, duration: number = 0) => {
  const date = dayjs(myDate);
  const userTimezone = useUsersStore((state) => state.userData?.timezone);
  const timezones = useListStore((state) => state.timezones);
  const offset = getTimeZoneOffsetInMinutes(userTimezone, timezones, date);
  const displayDate = dayjs(date).utcOffset(offset);
  const displayTime = getTimeFormat(displayDate);
  const displayDay = getDayFormat(displayDate);
  const displayDateFormat = getDateFormat(displayDate);
  const timeAgo = displayDate.fromNow();

  const displayDateWithDuration = displayDate.add(duration, 'minute');
  const displayTimeWithDuration = getTimeFormat(displayDateWithDuration);
  const displayDayWithDuration = getDayFormat(displayDateWithDuration);
  const displayDateWithDurationFormat = getDateFormat(displayDateWithDuration);

  return {
    timeAgo,
    displayDate,
    displayTime,
    displayDay,
    displayDateFormat,
    displayDateWithDuration,
    displayTimeWithDuration,
    displayDayWithDuration,
    displayDateWithDurationFormat,
  };
};
